import { buildQueryParamLink } from 'lib/helpers/urlHelper';
import { ISafeFetchConfigOptions, safeFetch } from 'lib/requests/fetch';
import _ from 'lodash';
import useSWR, {
  KeyedMutator,
  mutate,
  SWRConfiguration,
  SWRResponse,
} from 'swr';

// We are intentionally limiting the possible values of `key` to conform to our `fetcher` implementation (i.e. no objects).
export type FetchKey = string | [string, Record<any, any>];

export interface ISwrConfiguration extends SWRConfiguration {
  shouldFetch?: boolean;
}

export interface ISwrResponse<T> extends SWRResponse {
  data: T;

  /**
   * Returns `true` if there's an ongoing request and no "loaded data".
   * Fallback data and previous data are not considered "loaded data".
   **/
  isLoading: boolean;

  /**
   * Returns `true` if there's any request or revalidation loading,
   * regardless of whether "loaded data" exists or not.
   */
  isValidating: boolean;

  /**
   * Trigger a revalidation (mark the data as expired and trigger a refetch) for the resource.
   */
  mutate: KeyedMutator<T>;
}

export interface IResponseError extends Error {
  data?: unknown;
}

export const fetcher = (configOptions?: ISafeFetchConfigOptions) => {
  return async (key: FetchKey) => {
    const [baseUrl, queryParams] = _.isArray(key) ? key : [key, undefined];
    const url = _.isEmpty(queryParams)
      ? baseUrl
      : buildQueryParamLink(baseUrl, queryParams);
    const { data, error, message } = await safeFetch(url, {}, configOptions);
    if (error) {
      const e: IResponseError = new Error();
      e.data = data;
      e.message = message;
      throw e;
    } else {
      return data;
    }
  };
};

export const useFetch = <T>(
  key: FetchKey,
  options: ISwrConfiguration = {},
  configOptions?: ISafeFetchConfigOptions
): ISwrResponse<T> => {
  const { shouldFetch, ...rest } = options;
  const config: SWRConfiguration = { keepPreviousData: true, ...rest };
  const res = useSWR<T>(
    (shouldFetch ?? true) && key,
    fetcher(configOptions),
    config
  );

  // Clear cache for endpoint + all previous combinations of query params when `mutate()` is called.
  // This prevents the cache from containing stale data.
  const mutateEndpoint: KeyedMutator<T> = (...args) => {
    if (_.isArray(key)) {
      const [baseUrl] = key;
      mutate((key) => _.isArray(key) && key[0] === baseUrl, undefined, {
        revalidate: false,
      });
    }
    return res.mutate(...args);
  };

  return { ...res, mutate: mutateEndpoint };
};
